/**
 * analyticsCardGranularity — each chart card's OWN bucket width (VRG-512)
 *
 * ── GRANULARITY IS A DISPLAY PROPERTY, NOT A DATA PROPERTY ───────────────────────────
 *
 * 🚨 This replaces `AnalyticsFilters.granularity`, a page-level Redux field, and the swap
 * is the point rather than a refactor. Read the argument before putting it back.
 *
 * The date range answers WHAT AM I LOOKING AT — it scopes every KPI tile, every panel and
 * the ROI hero, so it is genuinely global and belongs in the toolbar beside the other
 * page-wide narrowings. Line-vs-bar and day-vs-month answer HOW IS THIS ONE SERIES DRAWN.
 * They are the same class of question, they change no number anywhere (a month bucket and
 * a day bucket sum to the same total), and they scope exactly one card. So the bucket
 * belongs where the mark toggle already is: in that card's header, keyed to that card.
 *
 * What the page-level field actually did, measured on a canvas holding a Scans chart and a
 * Certificates chart: changing "View by" fired TWO requests and re-bucketed BOTH cards. A
 * reader narrowing one chart to daily detail silently re-drew a neighbouring chart they had
 * not touched. That is the same SCOPE-vs-POSITION mismatch `AnalyticsFilters.chartDataType`
 * records for the Scans/Certificates pill it replaced — a per-card control promoted to
 * global for the only reason that ever promotes one, which is that only one card wanted it
 * at the time.
 *
 * ── WHY A MODULE STORE, AND NOT REDUX, AND NOT COMPONENT STATE ───────────────────────
 *
 * The deleted Redux field carried a real argument for living outside the chart: the CSV
 * export has to send the bucket the chart drew, and `analyticsExport` is a plain async
 * function with no access to a component's `useState`. A file headed with a card's title
 * but holding daily rows under a chart drawn in months is the export's signature failure —
 * the rows still sum to the same total, so nothing about the file looks wrong.
 *
 * That argument rules out COMPONENT state. It does not rule out a module store, which is
 * readable from anywhere by construction: `analyticsCardGranularityOverride(cardId)` is
 * exactly the call `buildChartCsv` needs, per card, with no React involved. So the export's
 * requirement is met without making the value page-global.
 *
 * The shape — a `Map<cardId, …>` behind `useSyncExternalStore` — is `markStore`'s
 * (`ScansByTime2.tsx`), deliberately and not incidentally. That store solved the identical
 * problem for the identical reason: the control renders into the card SHELL's header while
 * the chart renders into the shell's BODY, so the two are siblings with no common owner
 * below `AnalyticsPage`, and lifting either would mean threading state through the card
 * builder. Reusing the established mechanism rather than inventing a second one is §28/§36.
 *
 * ⚠️ It lives in its OWN leaf module rather than beside `markStore`, because
 * `analyticsExport` must import it and `markStore`'s home is a 2,300-line page component
 * that imports half of `components/analytics`. This module imports one type and nothing
 * else, so no import of it can close a cycle.
 *
 * ── `null` / ABSENT MEANS "DECIDE FOR ME", AND THAT IS NOT THE SAME AS A DEFAULT ─────
 *
 * A card with no entry is on AUTOMATIC: `analyticsEffectiveBucketUnit` derives a unit from
 * the range, so a readable chart never requires touching this. The store holds the
 * PREFERENCE and never the derived unit — writing the auto-picked unit on every range
 * change would make every reader a permanent overrider after their first date edit, the
 * control freezing at whatever the first range happened to need and silently ceasing to
 * adapt. `null` is what keeps "I have no preference" expressible, and it is what the X on
 * the control resets to.
 *
 * An override is NOT cleared when a range change makes it impossible (Year, then a
 * one-month range): it is preserved so widening the range restores it, and the control's
 * caption is what makes the temporary fallback visible. See `analyticsEffectiveBucketUnit`.
 *
 * ── SESSION-SCOPED: NOT PERSISTED INTO A SAVED VIEW ──────────────────────────────────
 *
 * Same call, for the same reason, as `markStore` and `hiddenSeriesStore` — both documented
 * as deliberately outside `useAnalyticsView`'s `currentState`. A bucket override is a claim
 * about the range that was on screen when it was made: a view saved at "View by: Day" over
 * a fortnight and reopened months later over three years would force ~1,100 daily points,
 * or fall back to a unit nobody chose, with nothing on screen saying why. Session-only
 * means an override answers "what do I want to look at right now", never "what did I once
 * decide about a range that no longer applies".
 *
 * If product direction later wants overrides to survive a reload, that is new persisted
 * state on `AnalyticsViewState` (a `cardBuckets: Record<string, AnalyticsChartBucketUnit>`
 * beside `cards`/`layouts`) — not a retrofit of this store, which is kept outside that
 * comparison exactly as its two precedents are.
 */
import { useCallback, useSyncExternalStore } from 'react'

import type { AnalyticsChartBucketUnit } from './analyticsDisplay'

const overrides = new Map<string, AnalyticsChartBucketUnit>()
const listeners = new Set<() => void>()
const emit = () => listeners.forEach(fn => fn())

const subscribe = (fn: () => void) => {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/**
 * ONE card's held preference — `null` when it is on Automatic.
 *
 * The plain, non-React read. `analyticsExport.buildChartCsv` calls this per card id so a
 * chart's CSV carries the bucket that card was drawn at; see this module's header for why
 * that requirement is what makes a module store the right shape.
 */
export const analyticsCardGranularityOverride = (
  cardId: string,
): AnalyticsChartBucketUnit | null => overrides.get(cardId) ?? null

/** Set or clear ONE card's preference. `null` returns it to Automatic. */
export const setAnalyticsCardGranularity = (
  cardId: string,
  unit: AnalyticsChartBucketUnit | null,
): void => {
  if (analyticsCardGranularityOverride(cardId) === unit) return
  if (unit === null) overrides.delete(cardId)
  else overrides.set(cardId, unit)
  emit()
}

/**
 * ── COMPOSING A SERIES ONTO A CARD MUST NOT RE-BUCKET IT ─────────────────────────────
 *
 * 🚨 The composed-card rule, and the reason this function exists at all.
 *
 * A composed card has ONE bucket, because it is one plot with one x-axis — two series
 * drawn at two bucket widths on shared axes would not be a comparison, it would be two
 * charts overlaid. So the question is only ever WHICH bucket the combined card gets, and
 * the answer is: THE ONE THE TARGET CARD ALREADY HAD. A compose is a drop ONTO an existing
 * card; the reader is adding to a chart they are already reading, so the chart they are
 * reading is what must survive. The dragged metric contributes a series, not a preference.
 *
 * The mechanism is a rename, and that is exactly what makes this necessary. A card's
 * identity ENCODES its series (`scans_count::chart` becomes
 * `scans_count+certificates_count::chart`), so a compose, a decompose and a map regroup all
 * rewrite the id of a card that has not moved. Without this, the new id would have no entry
 * and the card would silently drop to Automatic — a reader who had deliberately set Day
 * would drag one more series on and watch their chart re-bucket to Month, with no control
 * having been touched and nothing saying what happened. Since the marks still sum to the
 * same totals, the chart would look fine; it would simply be answering a different
 * question.
 *
 * Called from `useAnalyticsView.renameCardId`, which is the single choke point all three
 * gestures share — so the rule cannot be half-applied to compose and forgotten for
 * decompose.
 *
 * A no-op when the old id has no entry: a card on Automatic stays on Automatic, and the
 * new id is left ABSENT rather than written as `null`, so "no preference" has one
 * representation.
 */
export const renameAnalyticsCardGranularity = (oldId: string, newId: string): void => {
  const held = overrides.get(oldId)
  if (held === undefined) return
  overrides.delete(oldId)
  overrides.set(newId, held)
  emit()
}

/**
 * `[held preference, set it]` for ONE card. `null` is Automatic, not a missing value.
 *
 * `emit()` notifies every subscriber regardless of which id moved — `markStore`'s shape —
 * but the snapshot is a PRIMITIVE, so `useSyncExternalStore`'s `Object.is` check means a
 * card whose own entry did not change does not re-render. That is the property the
 * two-card proof turns on: changing one chart's bucket must not re-request the other's.
 */
export const useAnalyticsCardGranularity = (
  cardId: string,
): [AnalyticsChartBucketUnit | null, (next: AnalyticsChartBucketUnit | null) => void] => {
  const getSnapshot = useCallback(() => analyticsCardGranularityOverride(cardId), [cardId])
  const value = useSyncExternalStore(subscribe, getSnapshot)
  const set = useCallback(
    (next: AnalyticsChartBucketUnit | null) => setAnalyticsCardGranularity(cardId, next),
    [cardId],
  )
  return [value, set]
}
